import { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import type { DateRange } from 'react-day-picker';
import { isFuture, format, parse, endOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import type { ChatConversationForHistory, ChatConversationsQueryParams } from '@intone/shared';

import { DataTable, type Column, type FilterConfig } from '@/shared/ui/DataTable';
import { formatDateHistory } from '@/shared/lib/date';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { useChatConversationsForHistory } from '@/entities/chatConversation';
import { ChatConversationDetails } from '@/widgets/chatConversationDetails';
import { DispositionBadge } from '@/features/dispositionBadge';

import {
  useHashParams,
  validateParams,
  dateFormatStr,
} from '../model/useHashParams';

interface ChatHistoryContentProps {
  campaignId: number
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
}

export function ChatHistoryContent({ campaignId }: ChatHistoryContentProps) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { callId: conversationId } = useParams<{ callId?: string }>();

  const [hashParams, setHashParams] = useHashParams();
  const initFromHash = useRef(validateParams(hashParams));
  const dateRangeDerived = useRef(false);

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [autoQAFilter, setAutoQAFilter] = useState<string>('all');

  useEffect(() => {
    if (validateParams(hashParams)) {
      setDateRange({
        from: parse(hashParams.start, dateFormatStr, new Date()),
        to: parse(hashParams.end, dateFormatStr, new Date()),
      });
      dateRangeDerived.current = true;
    }
  }, [hashParams.start, hashParams.end]);

  useEffect(() => {
    if (!initFromHash.current) {
      dateRangeDerived.current = false;
      setDateRange(undefined);
    }
    initFromHash.current = false;
  }, [campaignId]);

  const queryParams: ChatConversationsQueryParams = useMemo(() => {
    const base: ChatConversationsQueryParams = {
      pageSize,
      pageId: currentPage,
      orderBy: sortField as ChatConversationsQueryParams['orderBy'],
      orderType: sortDirection,
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(customerFilter && { customerId: customerFilter }),
      ...(autoQAFilter !== 'all' && { autoQaYesMin: Number(autoQAFilter) }),
    };

    if (dateRangeDerived.current && hashParams.start && hashParams.end && validateParams(hashParams)) {
      const from = parse(hashParams.start, dateFormatStr, new Date());
      const to = parse(hashParams.end, dateFormatStr, new Date());
      base.startDate = from.toISOString();
      base.endDate = endOfDay(to).toISOString();
    }

    return base;
  }, [
    hashParams.start, hashParams.end,
    currentPage, pageSize, sortField, sortDirection,
    statusFilter, customerFilter, autoQAFilter, hash, campaignId
  ]);

  const { data, isLoading, isFetching, error } = useChatConversationsForHistory(campaignId, queryParams);
  const conversations: ChatConversationForHistory[] = data?.conversations || [];
  const totalCount = data?.totalCount || 0;
  const initialLoading = isLoading && !data;

  useEffect(() => {
    if (!dateRangeDerived.current && conversations.length > 0) {
      const timestamps = conversations
        .map(c => new Date(c.created_at).getTime())
        .filter(t => t > 0);
      if (timestamps.length > 0) {
        const minDate = new Date(Math.min(...timestamps));
        const maxDate = new Date(Math.max(...timestamps));
        setDateRange({ from: minDate, to: maxDate });
        dateRangeDerived.current = true;
      }
    }
  }, [conversations]);

  const columns: Column<ChatConversationForHistory>[] = useMemo(() => [
    {
      header: 'Customer',
      accessorKey: 'customer_id',
      sortField: 'customer_id',
      sortable: true,
      cell: (value: string | null) => value || '-',
      width: { min: 180, max: 220 }
    },
    {
      header: 'Created',
      accessorKey: 'created_at',
      sortField: 'created_at',
      sortable: true,
      cell: (value: string | null) => {
        if (!value) return '-'
        return formatDateHistory(new Date(value), 'MMM d, yyyy h:mm a')
      },
      width: { min: 180, max: 180 }
    },
    {
      header: 'Status',
      accessorKey: 'status',
      sortField: 'status',
      sortable: true,
      cell: (value: string | null) => (
        <Badge variant="outline" className={STATUS_COLORS[value ?? ''] ?? 'bg-gray-100 text-gray-800'}>
          {value || '-'}
        </Badge>
      ),
      width: { min: 90, max: 90 }
    },
    {
      header: 'Integration',
      accessorKey: 'integration_type',
      sortField: 'integration_type',
      sortable: true,
      cell: (value: string | null) => value || '-',
      width: { min: 120, max: 140 }
    },
    {
      header: 'Disposition',
      accessorKey: 'disposition_status',
      sortable: false,
      cell: (value: string | null) => <DispositionBadge disposition={value || 'TECH'} />,
      width: { min: 110, max: 120 }
    },
  ], []);

  const filters: FilterConfig[] = useMemo(() => [
    { type: 'text', key: 'customer', label: 'Customer', placeholder: 'Search by customer...' },
    {
      type: 'select', key: 'status', label: 'Status',
      options: [
        { value: 'all', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'closed', label: 'Closed' },
      ],
      noFilterValue: 'all',
    },
    {
      type: 'select', key: 'auto_qa_yes_min', label: 'Automated QA',
      options: [
        { value: 'all', label: 'All' },
        { value: '1', label: '1 flag or more' },
        { value: '2', label: '2 flags or more' },
        { value: '3', label: '3 flags or more' },
      ],
      noFilterValue: 'all',
    },
  ], []);

  const filterValues = useMemo(
    () => ({ customer: customerFilter, status: statusFilter, auto_qa_yes_min: autoQAFilter }),
    [customerFilter, statusFilter, autoQAFilter]
  );

  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'customer': setCustomerFilter(value); break
      case 'status': setStatusFilter(value); break
      case 'auto_qa_yes_min': setAutoQAFilter(value); break
    }
  }

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open && dateRange?.from && dateRange?.to) {
        setHashParams({ start: format(dateRange.from, dateFormatStr), end: format(dateRange.to, dateFormatStr) });
      }
    },
    [dateRange, setHashParams]
  );

  const handleRowClick = (row: ChatConversationForHistory) => {
    navigate(`/history/${row.id}${hash}`);
  }

  return (
    <>
      <label className="flex items-center gap-2 w-max mb-4">
        <span className="text-body font-medium whitespace-nowrap">Date Range:</span>
        <Popover onOpenChange={handleClose}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[260px] justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span>
                {!dateRange ? 'Select Date Range:' : (
                  <>
                    {dateRange.from && formatDateHistory(dateRange.from, 'LLL dd, y')}{dateRange.to ? ` - ${formatDateHistory(dateRange.to, 'LLL dd, y')}` : undefined}
                  </>
                )}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={dateRange?.to}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              disabled={(date) => isFuture(date)}
            />
          </PopoverContent>
        </Popover>
      </label>

      <div className="mt-4 overflow-hidden flex flex-col lg:flex-row gap-4">
        <div className={`flex-1 overflow-hidden ${isFetching && !initialLoading ? 'opacity-60 transition-opacity duration-200' : 'transition-opacity duration-200'}`}>
          <div className="overflow-x-auto">
            <DataTable<ChatConversationForHistory>
              columns={columns}
              data={conversations}
              loading={initialLoading}
              error={error?.message}
              pagination={{ currentPage, pageSize, totalCount }}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              sorting={{ sortField, sortDirection }}
              onSortChange={(sf, sd) => { setSortField(sf); setSortDirection(sd) }}
              filters={filters}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onRowClick={handleRowClick}
              selectedId={conversationId}
            />
          </div>
        </div>

        <div className="lg:flex-1 lg:min-w-[400px] lg:max-w-[600px]">
          <ChatConversationDetails conversationId={conversationId || null} />
        </div>
      </div>
    </>
  )
}
